import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator as CalcIcon, Check, ArrowRight, Sparkles } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const formatBRL = (n) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export default function Calculator() {
  const { services, content, config } = useSite();
  const [selected, setSelected] = useState({});

  const priced = services.filter((s) => Number(s.estimated_price) > 0);

  const total = useMemo(
    () => priced.reduce((acc, s) => acc + (selected[s.id] ? Number(s.estimated_price) : 0), 0),
    [selected, priced]
  );

  if ((content?.calculator_visible ?? "true") !== "true") return null;
  if (!priced.length) return null;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const min = total;
  const max = Math.round(total * 1.6);

  const selectedTitles = priced.filter((s) => selected[s.id]).map((s) => s.title);
  const wa = `https://wa.me/${config?.whatsapp}?text=${encodeURIComponent(
    `Olá! Simulei pelo site e tenho interesse em: ${selectedTitles.join(", ")}. Estimativa: ${formatBRL(min)} - ${formatBRL(max)}.`
  )}`;

  return (
    <section data-testid="calculator-section" className="section-padding bg-white relative overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-30 pointer-events-none"></div>
      <div className="dc-container relative">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">
              <CalcIcon className="w-4 h-4" /> Calculadora
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-dc-text mt-3 mb-5 leading-tight">
              {content?.calculator_title || "Simule seu orçamento em segundos"}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed mb-8">
              {content?.calculator_subtitle}
            </p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-dc-secondary to-dc-primary text-white rounded-2xl p-7 shadow-2xl shadow-dc-primary/20"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-200 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-dc-accent" /> Estimativa
              </div>
              {total > 0 ? (
                <>
                  <div className="font-heading text-3xl md:text-4xl font-extrabold">
                    {formatBRL(min)} <span className="text-blue-200 text-xl font-medium">a</span> {formatBRL(max)}
                  </div>
                  <div className="text-sm text-blue-200 mt-2 mb-5">
                    {selectedTitles.length} serviço(s) selecionado(s) • valor sujeito a complexidade
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to="/orcamento"
                      data-testid="calculator-cta-orcamento"
                      className="inline-flex items-center gap-2 bg-dc-accent hover:bg-dc-accent-hover text-white px-5 py-3 rounded-full font-semibold text-sm transition shadow-lg"
                    >
                      Fechar proposta <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                      href={wa}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="calculator-cta-whatsapp"
                      className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-5 py-3 rounded-full font-semibold text-sm transition"
                    >
                      Falar no WhatsApp
                    </a>
                  </div>
                </>
              ) : (
                <div className="text-blue-100 text-sm">Selecione ao menos um serviço para ver a estimativa.</div>
              )}
            </motion.div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-3 max-h-[480px] overflow-y-auto pr-2">
              {priced.map((s) => {
                const isSel = !!selected[s.id];
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`calc-service-${s.id}`}
                    onClick={() => toggle(s.id)}
                    className={`text-left p-4 rounded-xl border transition-all ${
                      isSel
                        ? "border-dc-primary bg-dc-primary/5 shadow-md"
                        : "border-slate-200 hover:border-dc-primary/40 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition ${
                          isSel ? "bg-dc-primary text-white" : "border-2 border-slate-300"
                        }`}
                      >
                        {isSel && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-semibold text-sm text-dc-text leading-snug">{s.title}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {s.price_unit || "a partir de"} <span className="text-dc-primary font-semibold">{formatBRL(Number(s.estimated_price))}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">
              * Valores indicativos. A proposta final considera escopo, prazo e complexidade reais.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
